import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { Dialog } from '@/ui/Dialog';
import { SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { POR_PAGINA, useHistorial, useReporte } from './api';

/**
 * Historial.tsx — opción 4 del módulo 6: qué se envió y a quién llegó.
 *
 * ## Dice cuántos está enseñando
 *
 * Pide `limit=30`. La base de desarrollo tenía **593 notificaciones** el
 * 2026-09-15, así que la tabla mostraba el 5 % sin decirlo y sin forma de ver
 * más. `/notifications/history` no devuelve un total —no es que el frontend lo
 * tire, es que no existe—, así que lo honesto es decir que son las 30 más
 * recientes. Saber el número exacto exige ampliar el endpoint: cambio de
 * contrato, anotado en la ficha.
 *
 * ## El reporte carga cuando se abre, no antes
 *
 * `useReporte` va con `enabled`: el vanilla pedía el reporte en el `onclick` y
 * construía el modal a mano. Aquí la consulta solo existe mientras el diálogo
 * está abierto, y su estado de carga y de error son los del sistema.
 */

/** Los estados que devuelve el servidor para un envío. */
function EstadoDelEnvio({ estado }: { estado: string }) {
  const tono =
    estado === 'completado' ? 'ok'
      : estado === 'error' ? 'danger'
        : estado === 'programada' ? 'warn'
          : 'neutral';
  const etiqueta =
    estado === 'completado' ? 'Completado'
      : estado === 'error' ? 'Error'
        : estado === 'programada' ? 'Programada'
          : estado === 'enviando' ? 'Enviando'
            : estado;
  return <Badge tone={tono}>{etiqueta}</Badge>;
}

export function Historial() {
  const { data, isLoading, isError, error, refetch } = useHistorial();
  const [reporteDe, setReporteDe] = useState<number | null>(null);

  if (isLoading) return <SkeletonFilas filas={6} columnas={5} />;

  if (isError) {
    return (
      <Card className="p-4">
        <EstadoError err={error} que="el historial de envíos" onReintentar={() => refetch()} />
      </Card>
    );
  }

  if (!data?.length) {
    return (
      <EstadoVacio
        icono="bi-inbox"
        titulo="Sin historial de envíos"
        descripcion="Los mensajes que envíes aparecerán aquí, con su estado de entrega."
      />
    );
  }

  return (
    <>
      <Card className="overflow-x-auto">
        <table className="w-full border-collapse text-3">
          <thead>
            <tr className="border-b border-line text-left text-2 text-muted">
              <th scope="col" className="px-3 py-2 font-semibold">Asunto</th>
              <th scope="col" className="px-3 py-2 font-semibold">Destinatarios</th>
              <th scope="col" className="px-3 py-2 font-semibold">Entrega</th>
              <th scope="col" className="px-3 py-2 font-semibold">Estado</th>
              <th scope="col" className="px-3 py-2 font-semibold">Enviado</th>
              <th scope="col" className="px-3 py-2 font-semibold">
                <span className="sr-only">Reporte</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((n) => (
              <tr key={n.id} className="border-b border-line last:border-0">
                <td className="px-3 py-2 font-medium text-ink">{n.title}</td>
                <td className="px-3 py-2 tabular-nums">{n.recipient_count}</td>
                <td className="px-3 py-2 text-2">
                  {n.status === 'completado' ? (
                    <>
                      <span className="text-ok-ink">{n.delivered} entregados</span>
                      {n.failed > 0 && (
                        <span className="text-danger-ink"> · {n.failed} fallidos</span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td className="px-3 py-2"><EstadoDelEnvio estado={n.status} /></td>
                <td className="px-3 py-2 text-2 text-muted">
                  {Utils.fmtDatetime(n.sent_at || n.created_at || '')}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setReporteDe(n.id)}
                    aria-label={`Ver el reporte de envío de «${n.title}»`}
                  >
                    <i className="bi bi-clipboard-data" aria-hidden="true" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {data.length >= POR_PAGINA && (
        <p className="mt-3 text-2 text-muted">
          Mostrando los {POR_PAGINA} envíos más recientes.
        </p>
      )}

      <DialogoDeReporte id={reporteDe} onCerrar={() => setReporteDe(null)} />
    </>
  );
}

/** El reporte de un envío: cuántos llegaron, cuántos fallaron y quién lo leyó. */
function DialogoDeReporte({ id, onCerrar }: { id: number | null; onCerrar: () => void }) {
  const { data, isLoading, isError, error, refetch } = useReporte(id);

  return (
    <Dialog
      abierto={id != null}
      onCambio={(v) => !v && onCerrar()}
      titulo="Reporte de envío"
      descripcion={data ? `Estado de entrega de «${data.title}».` : 'Cargando el detalle…'}
      ancho="max-w-2xl"
      pie={<Button onClick={onCerrar}>Cerrar</Button>}
    >
      {isLoading ? (
        <SkeletonFilas filas={5} columnas={4} />
      ) : isError ? (
        <EstadoError err={error} que="el reporte" onReintentar={() => refetch()} />
      ) : data ? (
        <>
          <div className="mb-3 flex flex-wrap gap-2">
            <Badge tone="neutral">Total: {data.total}</Badge>
            <Badge tone="ok">Entregados: {data.delivered}</Badge>
            <Badge tone="danger">Fallidos: {data.failed}</Badge>
            <Badge tone="info">Leídos: {data.read}</Badge>
          </div>

          <div className="max-h-[50vh] overflow-auto rounded-sm border border-line">
            <table className="w-full border-collapse text-3">
              <thead className="sticky top-0 bg-surface">
                <tr className="border-b border-line text-left text-2 text-muted">
                  <th scope="col" className="px-3 py-2 font-semibold">Colaborador</th>
                  <th scope="col" className="px-3 py-2 font-semibold">Correo</th>
                  <th scope="col" className="px-3 py-2 font-semibold">Entregado</th>
                  <th scope="col" className="px-3 py-2 font-semibold">Leído</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((i, n) => (
                  <tr key={`${i.email}-${n}`} className="border-b border-line last:border-0">
                    <td className="px-3 py-1.5">{i.full_name}</td>
                    <td className="px-3 py-1.5 text-2 text-muted">{i.email}</td>
                    {/* El icono es decorativo y la palabra viaja en `.sr-only`:
                        un tic verde no dice «entregado» a quien no lo ve, y el
                        color por sí solo no comunica para quien no distingue
                        verde de rojo. */}
                    <td className="px-3 py-1.5">
                      <i
                        className={
                          i.email_sent
                            ? 'bi bi-check-circle-fill text-ok-ink'
                            : 'bi bi-x-circle-fill text-danger-ink'
                        }
                        aria-hidden="true"
                      />
                      <span className="sr-only">{i.email_sent ? 'Entregado' : 'No entregado'}</span>
                    </td>
                    <td className="px-3 py-1.5">
                      {i.is_read ? (
                        <>
                          <i className="bi bi-eye" aria-hidden="true" />
                          <span className="sr-only">Leída</span>
                        </>
                      ) : (
                        <>
                          <span aria-hidden="true" className="text-muted">—</span>
                          <span className="sr-only">Sin leer</span>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </Dialog>
  );
}
